import { useState, useEffect } from "react";
import axios from "axios";
import {
  AlertTriangle, Loader2, X, ShieldAlert, Trash2, ArrowRight, CheckCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REQUIRED_PHRASE = "ERASE ALL DATA";

const axiosAuth = () => axios.create({
  withCredentials: true,
  headers: { Authorization: `Bearer ${localStorage.getItem("lf_session") || ""}` },
});

/**
 * Factory reset — three-step locked-down flow:
 *   1) Warning + acknowledgement checkbox
 *   2) Type exact phrase
 *   3) Final confirmation with countdown
 */
export default function FactoryResetModal({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [acknowledged, setAcknowledged] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1); setAcknowledged(false); setPhrase("");
    setError(""); setDone(null); setCountdown(5);
    setLoadingPreview(true);
    axiosAuth().post(`${API}/admin/factory-reset/preview`)
      .then((res) => setPreview(res.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load preview"))
      .finally(() => setLoadingPreview(false));
  }, [open]);

  useEffect(() => {
    if (step !== 3 || done) return;
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [step, countdown, done]);

  if (!open) return null;

  const totalRowsDeleted = preview ? preview.collections.reduce(
    (s, c) => s + (c.preserved ? 0 : Math.max(0, c.count)), 0,
  ) : 0;

  const submit = async () => {
    setError(""); setSubmitting(true);
    try {
      const res = await axiosAuth().post(`${API}/admin/factory-reset`, {
        confirmation_phrase: phrase,
        acknowledged_consequences: acknowledged,
      });
      setDone(res.data);
      // Notify parent — they may want to log out admin since admin_users is wiped
      setTimeout(() => onSuccess?.(res.data), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || "Reset failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center px-4 py-8 overflow-y-auto" onClick={() => !submitting && !done && onClose()} data-testid="factory-reset-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-red-700 to-red-900 px-6 py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert size={20} />
            <span className="font-bold text-lg">Factory Reset · Danger Zone</span>
          </div>
          {!submitting && !done && (
            <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg" data-testid="btn-close-factory-reset"><X size={16} /></button>
          )}
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="factory-reset-success">
            <div className="w-14 h-14 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle size={28} className="text-emerald-700" />
            </div>
            <h3 className="text-xl font-bold text-zinc-950">Factory reset complete</h3>
            <p className="text-sm text-zinc-600 mt-2">A backup snapshot was saved to <code className="bg-zinc-100 px-1 py-0.5 rounded text-xs">{done.backup_path}</code>.</p>
            <p className="text-xs text-zinc-500 mt-3">You'll be signed out and the bootstrap admin (Florin) can sign in via Google to recreate the first account.</p>
          </div>
        ) : step === 1 ? (
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3 mb-5">
              <AlertTriangle size={18} className="text-red-700 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-red-900 text-sm">This action cannot be undone</h3>
                <p className="text-xs text-red-800 mt-1 leading-relaxed">
                  All projects, proposals, payments, files, promoters, applications, and even admin users will be permanently deleted from the database. A JSON snapshot is saved to disk before wipe (recoverable only via manual server access).
                </p>
              </div>
            </div>

            {loadingPreview ? <div className="py-8 flex justify-center"><Loader2 size={20} className="animate-spin text-zinc-400" /></div>
              : preview ? (
                <>
                  <h4 className="font-semibold text-zinc-900 text-sm mb-2">What will be deleted</h4>
                  <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 mb-4 max-h-64 overflow-y-auto" data-testid="factory-reset-preview">
                    <table className="w-full text-xs">
                      <thead><tr className="text-zinc-500 border-b border-zinc-200">
                        <th className="text-left py-1">Collection</th>
                        <th className="text-right py-1">Rows</th>
                        <th className="text-right py-1">Status</th>
                      </tr></thead>
                      <tbody>
                        {preview.collections.map((c) => (
                          <tr key={c.name} className="border-b border-zinc-100 last:border-b-0">
                            <td className="py-1 font-mono">{c.name}</td>
                            <td className="py-1 text-right">{c.count}</td>
                            <td className="py-1 text-right">
                              {c.preserved ? <span className="text-emerald-700 font-semibold">PRESERVED</span> : <span className="text-red-700 font-semibold">DELETE</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-sm text-zinc-700 mb-4"><strong>{totalRowsDeleted.toLocaleString()}</strong> rows across {preview.collections.filter(c => !c.preserved).length} collections will be deleted.</div>
                </>
              ) : null
            }

            <label className="flex items-start gap-2 cursor-pointer text-sm text-zinc-700">
              <input type="checkbox" data-testid="checkbox-ack-consequences" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="mt-0.5" />
              <span>I understand this will permanently delete all business data, that admin accounts will be wiped, and that the platform will enter bootstrap mode requiring the Florin Google SSO account to recreate the first admin.</span>
            </label>

            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3" data-testid="factory-reset-error">{error}</div>}

            <div className="flex gap-2 mt-5 justify-end">
              <button onClick={onClose} className="px-5 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 rounded-full">Cancel</button>
              <button onClick={() => setStep(2)} disabled={!acknowledged || !preview} data-testid="btn-step-1-next"
                className="px-5 py-2 bg-red-700 text-white text-sm font-semibold rounded-full hover:bg-red-800 disabled:opacity-40 inline-flex items-center gap-2">
                I understand <ArrowRight size={14} />
              </button>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="p-6">
            <h3 className="font-bold text-zinc-950 mb-3">Type the confirmation phrase</h3>
            <p className="text-sm text-zinc-600 mb-4">To proceed, type the exact confirmation phrase below. This is your last chance to back out.</p>

            <div>
              <label className="block text-xs font-semibold text-zinc-600 mb-1">
                Type <code className="bg-zinc-950 text-white px-1.5 py-0.5 rounded">{REQUIRED_PHRASE}</code> exactly
              </label>
              <input data-testid="input-factory-reset-phrase" value={phrase} onChange={(e) => setPhrase(e.target.value)}
                autoComplete="off" autoCorrect="off" spellCheck="false"
                className="w-full px-3 py-2.5 bg-zinc-50 border border-red-300 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>

            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3" data-testid="factory-reset-error">{error}</div>}

            <div className="flex gap-2 mt-5 justify-end">
              <button onClick={() => setStep(1)} className="px-5 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 rounded-full">Back</button>
              <button onClick={() => { setStep(3); setCountdown(5); }} data-testid="btn-step-2-next"
                disabled={phrase !== REQUIRED_PHRASE}
                className="px-5 py-2 bg-red-700 text-white text-sm font-semibold rounded-full hover:bg-red-800 disabled:opacity-40 inline-flex items-center gap-2">
                Continue <ArrowRight size={14} />
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Trash2 size={18} className="text-red-700" />
                <h3 className="font-bold text-red-900 text-base">About to factory reset</h3>
              </div>
              <p className="text-sm text-red-800 leading-relaxed">
                This will execute the wipe immediately. No further confirmation. {totalRowsDeleted.toLocaleString()} rows will be deleted.
              </p>
            </div>

            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3" data-testid="factory-reset-error">{error}</div>}

            <div className="flex gap-2 justify-end items-center">
              <button onClick={() => setStep(2)} disabled={submitting} className="px-5 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 rounded-full">Back</button>
              <button onClick={submit} disabled={submitting || countdown > 0} data-testid="btn-execute-factory-reset"
                className="px-6 py-3 bg-red-800 text-white text-sm font-bold rounded-full hover:bg-red-900 disabled:opacity-40 inline-flex items-center gap-2">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Resetting…</>
                  : countdown > 0 ? <>Wait {countdown}s</>
                  : <><Trash2 size={14} /> Execute factory reset</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
